import React from "react";
import styled from "@emotion/styled";
import gql from "graphql-tag";
import { useQuery } from "react-apollo";
import { use100vh, useToggle } from "@hooks";
import { RailsUrl } from "@services";
import { Box, Container, Flex, Text, Circle, FontAwesome } from "@atoms";
import { ImageListItem } from "@molecules";
import { TeamOnboardingChecklistItem } from "@types";

// == Types ================================================================

interface IProps {
  fundingUrl: string;
  isMobile: boolean;
}

// == Constants ============================================================

OnboardingStepper.fragment = gql`
  fragment TeamOnboardingFragment on Viewer {
    id
    activeCampaign {
      id
    }
    teamOnboardingInfo {
      isActive
      isOnboardingActive
      isOnboardingChecklistActive
      type
      currentStep
      currentChecklistItem
      onboardingSteps {
        id
        name
        isCompleted
      }
      onboardingChecklistItems {
        id
        name
        description
        isCompleted
      }
    }
  }
`;

const QUERY = gql`
  query TeamOnboardingStepperQuery {
    Viewer {
      id
      ...TeamOnboardingFragment
    }
  }
  ${OnboardingStepper.fragment}
`;

const getChecklistUrl = (
  checklistItem: TeamOnboardingChecklistItem,
  fundingUrl: string,
  campaignId?: string
) => {
  switch (checklistItem) {
    case TeamOnboardingChecklistItem.CAPTAIN_INVITE_TEAM:
    case TeamOnboardingChecklistItem.TEAM_MEMBER_INVITE_SUPPORTERS:
      return `${fundingUrl}/teams/invite`;
    case TeamOnboardingChecklistItem.CAPTAIN_SHOP:
    case TeamOnboardingChecklistItem.TEAM_MEMBER_SHOP:
      return RailsUrl.storeUrl({ id: "firstshop", campaignId });
    case TeamOnboardingChecklistItem.CAPTAIN_SUBMIT_PAYMENT_INFO:
      if (!campaignId) return null;
      return RailsUrl.newBeneficiaryUrl(campaignId);
    case TeamOnboardingChecklistItem.CAPTAIN_SET_ROSTER:
      return `${fundingUrl}/teams/team-roster/edit`;
    default:
      return null;
  }
};

const BAR_HEIGHT = 75;

// == Component ============================================================

export function OnboardingStepper({ fundingUrl, isMobile }: IProps) {
  const [isOpen, toggleIsOpen] = useToggle(false);

  const viewportHeight = use100vh();
  const bottomOffset = isMobile ? 54 : 0;
  const height = viewportHeight ? viewportHeight - BAR_HEIGHT-bottomOffset : `calc(100vh - ${BAR_HEIGHT}px)`;

  const { data } = useQuery(QUERY);
  const viewer = data?.Viewer;
  if (!viewer) return null;
  if (viewer?.teamOnboardingInfo.isOnboardingActive) return null;
  if (!viewer?.teamOnboardingInfo.isOnboardingChecklistActive) return null;

  const { onboardingSteps, onboardingChecklistItems } = viewer.teamOnboardingInfo;
  const currentStep =
    viewer.teamOnboardingInfo.currentStep ?? viewer.teamOnboardingInfo.currentChecklistItem;
  const checklistItemsWithUrls = onboardingChecklistItems.map((item) => {
    return { ...item, url: getChecklistUrl(item.id, fundingUrl, viewer.activeCampaign?.id) };
  });
  const steps = [...onboardingSteps, ...checklistItemsWithUrls];
  const currentIndex = steps.findIndex((step) => step.id === currentStep);
  return (
    <>
      <style
        // eslint-disable-next-line react/no-danger
        //  overflow: ${isOpen ? "hidden" : "auto"}
        dangerouslySetInnerHTML={{
          __html: `
            body { padding-bottom: 80px; overflow: ${isOpen ? "hidden" : "initial"}; }
            @media screen and (min-width: 640px) {
              body {
                overflow: initial;
              }
            }
`,
        }}
      />
      <Box bg="white" bottom={bottomOffset} left={0} position="fixed" right={0} zIndex={3} overflowY="auto">
        <Box bg="orange" py="12px" onClick={toggleIsOpen} style={{ cursor: "pointer" }}>
          <Container>
            <Flex width="100%">
              <Box flex={1} mr={3}>
                {!!currentStep && (
                  <Text color="white" fontSize={3}>
                    {steps[currentIndex].name}
                  </Text>
                )}
                <Flex borderRadius="10px" style={{ overflow: "hidden" }} width="100%">
                  {steps.map((step, index) => {
                    return (
                      <Box
                        bg="white"
                        height="10px"
                        key={step.id}
                        mr={index + 1 === steps.length ? 0 : "2px"}
                        opacity={index <= currentIndex ? 1 : 0.5}
                        style={{ flexGrow: 1 }}
                      />
                    );
                  })}
                </Flex>
              </Box>
              <Box style={{ flexShrink: 0 }}>
                <Box pt={2} px={[3, 0]} width={["auto", "170px"]}>
                  <FontAwesome
                    icon={isOpen ? "chevron-down" : "chevron-up"}
                    size="3x"
                    style={{
                      marginLeft: "auto",
                      cursor: "pointer",
                      maxWidth: "25px",
                      color: "white",
                    }}
                  />
                </Box>
              </Box>
            </Flex>
          </Container>
        </Box>
        <Box
          minHeight={[isOpen ? height : 0, 0]}
          maxHeight={[0, isOpen ? 1000 : 0]}
          style={{
            transition: "max-height 0.5s ease, min-height 0.5s ease",
            overflowY: "auto",
          }}
        >
          <Box py={3}>
            <Container>
              {steps.map((step, index) => {
                return (
                  <Box key={step.id} mb={3}>
                    <StyledImageListItem
                      href={step.url}
                      leftComponent={
                        step.isCompleted ? (
                          <Circle bg="orange" width="100%">
                            <FontAwesome color="white" icon="check" />
                          </Circle>
                        ) : (
                          <Circle bg="white" border="silver" width="100%" />
                        )
                      }
                      subText={currentStep && currentStep === step.id && steps[index].description}
                      leftComponentProps={{ width: "40px", height: "40px" }}
                      text={step.name}
                    />
                  </Box>
                );
              })}
            </Container>
          </Box>
        </Box>
      </Box>
    </>
  );
}

// == Styles ===============================================================
const StyledImageListItem = styled(ImageListItem)`
  .image-list-item-react__title {
    color: ${({ href }) => (href ? "#666" : "#AAA")};
    &:hover {
      color: ${({ theme, href }) => (href ? theme.colors.primary : "#AAA")};
    }
  }
  .image-list-item__subtext {
    white-space: initial;
    display: block;
    line-height: 1.3;
    margin-top: 3px;
  }
`;
